/**
 * Einwilligungsgesteuertes Laden der Drittanbieter-Tracker (CCM19).
 *
 * Ohne Einwilligung wird KEIN Tracker-Script geladen und KEIN Drittanbieter-Cookie
 * gesetzt (§ 25 Abs. 1 TDDDG, Art. 6 Abs. 1 lit. a DSGVO).
 *
 * WARUM `acceptedEmbeddings` und NICHT `acceptedCookies`:
 * Am 13.08.2026 gegen die Live-Konfiguration gemessen (siehe PR):
 *   - Nach „Alles ablehnen" enthält `CCM.acceptedCookies` weiterhin `_pk_id.25.1d73`,
 *     weil dieser Cookie im CCM19-Konto der Kategorie „Technisch notwendig" zugeordnet
 *     ist. Eine Namensprüfung auf `_pk_id` startet Matomo also trotz Ablehnung.
 *   - `CCM.acceptedEmbeddings` ist dienstgenau und nach „Alles ablehnen" nur
 *     `[{ name: 'Sonstiges' }]` — keiner der Tracker ist enthalten.
 *   - Das Event `ccm19CookieAccepted` feuert in dieser Konfiguration NIE (in allen
 *     Szenarien 0×). Eine rein eventbasierte Lösung würde beide Tracker dauerhaft
 *     abschalten; die Auswertung läuft deshalb über `acceptedEmbeddings`.
 *
 * CCM19-JavaScript-API: https://github.com/CCM19/ccm19doks/blob/master/docs/api/javascript-apis.md
 */

const META_PIXEL_ID = '1695447394825844';

/** Matomo-Konfiguration wie bisher aus den Build-Env-Variablen (siehe packages/frontend/.env). */
const MATOMO_URL = import.meta.env.VITE_MATOMO_URL as string | undefined;
const MATOMO_SITE_ID = import.meta.env.VITE_MATOMO_SITE_ID as string | undefined;

/**
 * Embedding-IDs aus dem CCM19-Konto (Primärschlüssel). Die Namen dienen als
 * Fallback, falls im Konto neue Embeddings mit anderer ID angelegt werden.
 * „Piwik Pro" ist bewusst NICHT enthalten — anderes Produkt, eigener Dienst.
 */
const META_EMBEDDING_IDS = ['6a2725670681d9c8c70a7c12', '6a4851a70d28817c340570a5'];
const META_EMBEDDING_NAMES = ['meta pixel', 'facebook'];

const MATOMO_EMBEDDING_IDS = ['6a2a15e118358e344004bf42', '69dcb45ef9371e1166073a04'];
const MATOMO_EMBEDDING_NAMES = ['matomo'];

/**
 * Zusatzsperre für das Meta-Pixel.
 *
 * Das CCM19-Konto kennt derzeit nur die Zwecke „Technisch notwendig" und
 * „Analyse / Statistiken". Wer nur der Statistik zustimmt, bekommt von CCM19
 * trotzdem die Embeddings „Meta Pixel" und „Facebook" als akzeptiert gemeldet —
 * Statistik-only und Alles-akzeptieren sind clientseitig nicht unterscheidbar.
 * Ein Marketing-Tracker darf so nicht starten (Art. 6 Abs. 1 lit. a DSGVO,
 * § 25 Abs. 1 TDDDG).
 *
 * Deshalb verlangt das Meta-Pixel zusätzlich einen EIGENEN, angehakten
 * Marketing-Zweck im Banner. Existiert keiner, bleibt das Pixel aus
 * (fail-closed). Sobald im CCM19-Konto eine Kategorie „Marketing" (oder
 * „Werbung") mit den Meta-Embeddings angelegt ist, greift die Prüfung
 * automatisch — ohne weitere Codeänderung.
 */
const MARKETING_PURPOSE_PATTERN = /marketing|werbung|werbe/i;

interface Ccm19Embedding {
  id?: string;
  name?: string;
}

declare global {
  interface Window {
    CCM?: {
      acceptedCookies?: string[];
      acceptedEmbeddings?: Ccm19Embedding[];
      consent?: boolean;
      fullConsentGiven?: boolean;
      ucid?: string | null;
    };
    fbq?: any;
    _fbq?: any;
    _paq?: any[];
  }
}

let metaPixelInitialized = false;
let matomoInitialized = false;

/**
 * Debug-Ausgabe der CCM19-Zustände. Aktivierung ohne Deploy über die Konsole:
 *   localStorage.setItem('ccmDebug', '1'); location.reload();
 */
function isDebug(): boolean {
  try {
    return localStorage.getItem('ccmDebug') === '1';
  } catch {
    return false;
  }
}

function debugLog(...args: unknown[]): void {
  if (isDebug()) console.log('[trackerConsent]', ...args);
}

/**
 * Prüft, ob CCM19 ein bestimmtes Embedding (= Dienst) als akzeptiert meldet.
 * Ohne Einwilligung ist `acceptedEmbeddings` leer, daher ist die Prüfung
 * beim Seitenstart per Definition negativ.
 */
export function isServiceAccepted(ids: string[], nameNeedles: string[]): boolean {
  const accepted = window.CCM?.acceptedEmbeddings;
  if (!Array.isArray(accepted)) return false;
  return accepted.some((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    if (typeof entry.id === 'string' && ids.includes(entry.id)) return true;
    const name = typeof entry.name === 'string' ? entry.name.toLowerCase() : '';
    return !!name && nameNeedles.some((needle) => name.includes(needle));
  });
}

/** Meta-Pixel nachladen und initialisieren. Idempotent. */
export function initMetaPixel(): void {
  if (metaPixelInitialized) return;
  metaPixelInitialized = true;
  debugLog('initMetaPixel()');

  // Standard-Meta-Stub: sammelt Aufrufe in einer Queue, bis fbevents.js geladen ist.
  if (!window.fbq) {
    const fbq: any = function (...args: unknown[]) {
      fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }

  window.fbq('init', META_PIXEL_ID);
  // Consent-Grant MUSS vor dem ersten track()-Aufruf erfolgen.
  window.fbq('consent', 'grant');
  window.fbq('track', 'PageView');
}

/**
 * Matomo nachladen und initialisieren. Idempotent.
 *
 * Cookie-los betrieben: [`disableCookies`] verhindert, dass Matomo `_pk_id`/`_pk_ses`
 * auf dem Endgerät ablegt. Damit wird nichts auf dem Gerät gespeichert oder daraus
 * gelesen — § 25 Abs. 1 TDDDG greift nicht, und die Messung ist auch für Besucher
 * möglich, die nicht einwilligen. Die serverseitige IP-Anonymisierung bleibt aktiv.
 *
 * Preis dafür: keine Wiedererkennung über mehrere Besuche hinweg.
 * Muss synchron zu `public/tracker-consent.js` gehalten werden.
 */
export function initMatomo(): void {
  if (matomoInitialized) return;
  if (!MATOMO_URL || !MATOMO_SITE_ID) {
    debugLog('initMatomo() übersprungen — VITE_MATOMO_URL/VITE_MATOMO_SITE_ID nicht gesetzt');
    return;
  }
  matomoInitialized = true;
  debugLog('initMatomo()', MATOMO_URL, MATOMO_SITE_ID);

  const _paq = (window._paq = window._paq || []);
  _paq.push(['disableCookies']);
  _paq.push(['setDoNotTrack', true]);
  _paq.push(['trackPageView']);
  _paq.push(['enableLinkTracking']);
  _paq.push(['setTrackerUrl', MATOMO_URL + 'matomo.php']);
  _paq.push(['setSiteId', MATOMO_SITE_ID]);

  const script = document.createElement('script');
  script.async = true;
  script.src = MATOMO_URL + 'matomo.js';
  document.head.appendChild(script);
}

/**
 * Sucht im Banner nach einem eigenständigen Marketing-Zweck und gibt zurück,
 * ob dieser angehakt ist. Ist kein solcher Zweck vorhanden (aktueller Stand des
 * CCM19-Kontos) oder das Banner-Markup noch nicht da, ist das Ergebnis `false`.
 */
export function isMarketingPurposeAccepted(): boolean {
  const inputs = document.querySelectorAll<HTMLInputElement>('input[id^="ccm-purpose-"]');
  for (const input of Array.from(inputs)) {
    const label = document.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    const text = (label?.textContent || input.closest('div, li, section')?.textContent || '').trim();
    if (MARKETING_PURPOSE_PATTERN.test(text)) {
      debugLog('Marketing-Zweck gefunden:', text.slice(0, 60), '→ angehakt:', input.checked);
      return input.checked;
    }
  }
  debugLog('kein eigener Marketing-Zweck im Banner → Meta-Pixel bleibt gesperrt');
  return false;
}

/** Startet die Tracker, für die eine dienstgenaue Einwilligung vorliegt. */
export function checkAndInit(): void {
  debugLog('checkAndInit()', {
    consent: window.CCM?.consent,
    embeddings: window.CCM?.acceptedEmbeddings,
  });
  if (
    isServiceAccepted(META_EMBEDDING_IDS, META_EMBEDDING_NAMES) &&
    isMarketingPurposeAccepted()
  ) {
    initMetaPixel();
  }
  if (isServiceAccepted(MATOMO_EMBEDDING_IDS, MATOMO_EMBEDDING_NAMES)) initMatomo();
}

/** Registriert die CCM19-Listener. Idempotent (mehrfacher Import schadet nicht). */
export function registerConsentListeners(): void {
  if ((window as any).__trackerConsentRegistered) return;
  (window as any).__trackerConsentRegistered = true;

  // Auswahl gespeichert / Banner geschlossen — der eigentliche Consent-Zeitpunkt.
  window.addEventListener('ccm19WidgetClosed', checkAndInit);

  // Zurückkehrender Besucher: CCM19 stellt den gespeicherten Consent wieder her.
  // Ohne Einwilligung ist `acceptedEmbeddings` hier leer, es startet also nichts.
  window.addEventListener('ccm19WidgetLoaded', checkAndInit);

  // Feuert in der aktuellen CCM19-Konfiguration nicht, bleibt aber als Absicherung
  // erhalten, falls im Konto später eventfähige Kategorien angelegt werden.
  window.addEventListener('ccm19CookieAccepted', checkAndInit);

  // Falls CCM19 bereits vor diesem Modul initialisiert war, sind die Events
  // schon durch — deshalb einmalig direkt prüfen (ohne Consent ergebnislos).
  checkAndInit();
}

registerConsentListeners();
